import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { Icon, Loading, type IconName } from "./ds";
import { api, type AuthUser } from "./lib/api";
import { displayName, signOut, useAuth } from "./lib/hooks/useAuth";
import { useFleetEvents } from "./lib/hooks/useFleetEvents";
import { LoginPage, NoAccessPage } from "./pages/LoginPage";

// App-Shell im echten Design-System-DOM (.ds-app / .ds-subnav / .ds-main), nicht
// mehr als eigener Tailwind-Nachbau: nur dieses DOM bringt das mobile Verhalten
// des DS mit (Drawer unter 1024 px, Safe-Area-Insets, nur .ds-content scrollt).
// Wir fahren die Variante `subnav-only` — eine Ebene Navigation, keine Icon-Rail,
// da Spyship fünf Top-Level-Bereiche und keine Unterbereiche hat.

const NAV: { to: string; label: string; icon: IconName }[] = [
  { to: "/fleet", label: "Fleet", icon: "fleet" },
  { to: "/approvals", label: "Enrollment", icon: "authorize" },
  { to: "/images", label: "Images & Modelle", icon: "box" },
  { to: "/schedule", label: "Geplante Updates", icon: "timer" },
  { to: "/wireguard", label: "WireGuard", icon: "shield" },
  { to: "/backups", label: "Backups", icon: "data" },
  { to: "/blocklist", label: "MAC-Blockliste", icon: "lock" },
  { to: "/audit", label: "Audit", icon: "log" },
];

/** Ab hier klappt das DS die Navigation in einen Drawer (dashboard.css). */
const MOBILE_QUERY = "(max-width: 1023px)";

/** Kürzel für den Avatar — „Jan Kahrs“ → „JK“, sonst der erste Buchstabe. */
function initials(user: AuthUser): string {
  const parts = displayName(user).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.[0] ?? "?").toUpperCase();
}

/** Angemeldeter Nutzer + Abmelden am Fuß der Navigation. */
function SessionFooter({ user }: { user: AuthUser }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="ds-subnav-footer spy-session">
      <div className="ds-org-avatar">{initials(user)}</div>
      <div className="ds-org-meta">
        <div className="ds-org-name">{displayName(user)}</div>
        {user.email && <div className="ds-org-role">{user.email}</div>}
      </div>
      <button
        type="button"
        className="ds-icon-btn"
        disabled={busy}
        title="Abmelden"
        aria-label="Abmelden"
        onClick={() => {
          setBusy(true);
          void signOut();
        }}
      >
        <Icon name="logout" size={16} />
      </button>
    </div>
  );
}

export function App() {
  const auth = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Der Drawer ist ein reiner Mobile-Zustand. Beim Wechsel auf Desktop-Breite
  // zurücksetzen, sonst steht beim nächsten Verkleinern das Menü offen da.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const sync = () => {
      if (!mq.matches) setMenuOpen(false);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Escape schließt den Drawer (Tastatur am Tablet / schmales Desktop-Fenster).
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Live-Zähler offener Freigaben → Badge in der Nav (Staff sieht neue Router).
  // Einmal seeden, danach pusht der Fleet-Stream den Zähler — kein Poll.
  const [pending, setPending] = useState(0);
  const authed = auth.status === "authenticated";
  useEffect(() => {
    // Ohne Session hat jede Abfrage nur einen Effekt: einen 401.
    if (!authed) return;
    api
      .listEnrollments("pending")
      .then((r) => setPending(r.length))
      .catch(() => undefined);
  }, [authed]);
  useFleetEvents({ onEnrollment: setPending, enabled: authed });

  // Bis der Auth-Zustand feststeht, wird nichts von der App gezeigt — sonst
  // rennen die Seiten in 401er und der Nutzer sieht kurz eine kaputte Ansicht.
  if (auth.status === "loading")
    return <Loading className="bg-app" minHeight="100vh" label="Anmeldung wird geprüft …" />;
  if (auth.status === "anonymous") return <LoginPage />;
  if (auth.status === "forbidden") return <NoAccessPage user={auth.user} />;

  const current = NAV.find((n) => location.pathname.startsWith(n.to));

  return (
    <div className="ds-app" data-nav="subnav-only" data-mobile-open={menuOpen ? "true" : "false"}>
      <aside className="ds-subnav" id="spy-nav" aria-label="Hauptnavigation">
        <div className="ds-subnav-header spy-subnav-header">
          {/* Logo liegt in public/ statt in src/assets/ — dieselbe Datei dient als
              Favicon (index.html), so gibt es nur eine Quelle für die Wortmarke. */}
          <img src="/spyship-logo.svg" alt="Eulektro Spyship" className="spy-logo" />
          <button
            type="button"
            className="ds-icon-btn spy-drawer-close"
            aria-label="Menü schließen"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <nav className="ds-subnav-scroll">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => "ds-nav-item" + (isActive ? " active" : "")}
              // Drawer zu, sonst verdeckt er genau die Seite, auf die der Nutzer
              // gerade getippt hat. Am Klick statt in einem Effekt auf die
              // Route: der offene Drawer liegt über der Seite, anklickbar ist
              // darin nur die Navigation selbst — ein anderer Weg zu einer
              // neuen Route existiert in diesem Zustand nicht.
              onClick={() => setMenuOpen(false)}
            >
              <Icon name={n.icon} size={16} />
              <span className="spy-nav-label">{n.label}</span>
              {n.to === "/approvals" && pending > 0 && <span className="ds-count">{pending}</span>}
            </NavLink>
          ))}
        </nav>

        <SessionFooter user={auth.user} />
      </aside>

      <div className="ds-main">
        <header className="ds-topbar">
          <button
            type="button"
            className="ds-mobile-trigger"
            aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={menuOpen}
            aria-controls="spy-nav"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <Icon name="menu" size={20} />
          </button>
          {/* Desktop: Breadcrumb. Auf Mobile blendet das DS .ds-crumbs aus —
              dort trägt stattdessen die Wortmarke die Topbar. */}
          <div className="ds-crumbs">
            <span>Spyship</span>
            <span className="ds-sep">
              <Icon name="chevronRight" size={12} />
            </span>
            <span className="ds-cur">{current?.label ?? "Übersicht"}</span>
          </div>
          <img src="/spyship-logo.svg" alt="Eulektro Spyship" className="spy-topbar-logo" />
        </header>

        <div className="ds-content">
          <Outlet />
        </div>
      </div>

      {/* Nur unter 1024 px sichtbar (DS), fängt den Tap neben dem Drawer ab. */}
      <div className="ds-mobile-backdrop" role="presentation" onClick={() => setMenuOpen(false)} />
    </div>
  );
}
